<template>
  <div>
    <CommonHeader>
      <div slot="submenu-center">
        <filter-field class="mt-2" :label="$t('Filter')" :value="filter" @input="updateFilter" />
      </div>
    </CommonHeader>
    <main>
      <div class="container">
        <transition-group class="project-list" name="list" tag="div">
          <div key="new" id="new-project-box" class="project-box" v-if="hasSimplePermissions('createProject')">
            <div class="project-icon">
              <img src="static/projecticonadd.png" />
            </div>
            <h2 class="project-box-title">
              {{ $t('New Project') }}
            </h2>
            <h3>&nbsp;</h3>

            <div class="projectMenu">
              <div class="projectMenuContents">
                <router-link :to="{ name: 'AddProject' }">
                  <h2>
                    <b-icon icon="plus-circle-fill" />
                    {{ $t('New Project') }}
                  </h2>
                </router-link>
                <router-link :to="{ name: 'ProjectPlanEdit', params: { _id: 'standard' } }">
                  <IsAdmin>
                    <h3>
                      <b-icon icon="pencil-fill" />
                      {{ $t('Edit Standard Project') }}
                    </h3>
                  </IsAdmin>
                </router-link>
                <a v-if="hasSimplePermissions('importProject')">
                  <h3 @click="selectProjectFile">
                    <b-icon icon="archive-fill" />
                    {{ $t('Import Project') }}
                  </h3>
                </a>
              </div>
            </div>
          </div>

          <div v-for="project in filteredProjects" :key="project._id" class="project-box">
            <b-icon
              v-if="project.lockedForEditingBy && project.lockedForEditingBy !== username"
              icon="lock-fill"
              class="projectIndicator"
            />
            <b-icon
              v-if="project.lockedForEditingBy && project.lockedForEditingBy === username"
              icon="file-lock2"
              class="projectIndicator"
            />
            <div class="project-icon">
              <img
                :src="project.projectIcon ? project.projectIcon : 'https://boon.h2entreprenad.se/icons/icon_noname.jpg'"
              />
            </div>
            <h2 class="project-box-title">{{ project.projectNumber }} {{ project.title }}</h2>
            <h3>{{ project.projectStartdate }}&nbsp;-&nbsp;{{ project.projectEnddate }}</h3>
            <div class="projectMenu">
              <div class="projectMenuContents">
                <router-link :to="{ name: projectLink, params: { _id: project._id } }">
                  <h1>
                    <b-icon icon="arrow-right-short" scale="1.5" />
                    {{ $t('Show Project') }}
                  </h1>
                </router-link>
                <br />
                <a v-if="hasSimplePermissions('duplicateProject')" @click="confirm('confirmDuplicateDialog', project)">
                  <b-icon icon="files" class="project-action-icon" flip-h />
                </a>
                <a v-if="hasSimplePermissions('getProjectJson')" @click="saveProjectToDisk(project)">
                  <b-iconstack class="project-action-icon">
                    <b-icon stacked icon="dash" shift-v="-6" scale="1.5" />
                    <b-icon stacked icon="shift-fill" scale="0.7" flip-v shift-v="2" />
                  </b-iconstack>
                </a>
                <a v-if="hasRole('admin')" @click="confirm('confirmArchivingDialog', project)">
                  <b-icon icon="trash-fill" class="project-action-icon" />
                </a>
              </div>
            </div>
          </div>
        </transition-group>
        <div class="phone-project">
          <ProjectSlide :projectData="filteredProjects" />
        </div>

        <div v-if="projectList.length === 0">
          {{ $t('No projects available') }}
        </div>
      </div>
      <modal
        name="confirmArchivingDialog"
        classes="modalBoon"
        :clickToClose="false"
        transition="nice-modal-fade"
        :delay="100"
        :width="450"
        :height="250"
      >
        <h2>{{ $t('Archive Project?') }}</h2>

        <p>{{ $t('Are you sure you want to archive the project {title}?', { title: selectedProject.title }) }}</p>

        <ul class="modalButtons">
          <li>
            <button class="darkButton" @click="archiveProject()">{{ $t('Archive the Project') }}</button>
          </li>
          <li>
            <button class="lightButton" @click="$modal.hide('confirmArchivingDialog')">
              {{ $t("Don't archive") }}
            </button>
          </li>
        </ul>
      </modal>
      <modal
        name="confirmDuplicateDialog"
        classes="modalBoon"
        :clickToClose="false"
        transition="nice-modal-fade"
        :delay="100"
        :width="450"
        :height="200"
      >
        <h2>{{ $t('Duplicate Project?') }}</h2>

        <p>
          {{
            $t('Are you sure you want to create a duplicate of the project {title}?', { title: selectedProject.title })
          }}
        </p>
        <ul class="modalButtons">
          <li>
            <button class="darkButton" @click="onDuplicateProject()">{{ $t('Create Duplicate') }}</button>
          </li>
          <li>
            <button class="lightButton" @click="$modal.hide('confirmDuplicateDialog')">
              {{ $t("Don't Duplicate") }}
            </button>
          </li>
        </ul>
      </modal>
    </main>
    <input type="file" ref="openFile" accept="text/json" style="display: none" @change="loadProjectsFromDisk" />

    <CommonFooter />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { saveAs } from 'file-saver';
import debounce from 'debounce';

import CommonFooter from '@/components/toolbar/CommonFooter';
import CommonHeader from '@/components/toolbar/CommonHeader';
import IsAdmin from '@/components/toolbar/IsAdmin';
import FilterField from '@/components/organisation/FilterField';
import ProjectSlide from './ProjectSlide';

import * as Roles from '@/store/roles';
import getEscapedRegExpText from '@/utils/getEscapedRegExpText';

export default {
  name: 'Projectlist',

  metaInfo() {
    return {
      titleTemplate: 'Boon',
    };
  },

  data() {
    return {
      confirmArchivingDialog: false,
      selectedProject: { title: '' },
      filter: '',
    };
  },

  methods: {
    ...mapActions(['selectproject', 'saveProjectProperties', 'getProjectJson', 'importProject', 'duplicateProject']),
    onDuplicateProject: function () {
      this.$modal.hide('confirmDuplicateDialog');
      this.duplicateProject({ project: this.selectedProject });
    },
    confirm: function (dialog, project) {
      this.selectedProject = project;
      this.$modal.show(dialog, { title: 'Archive Project?', project });
    },
    archiveProject: function () {
      this.$modal.hide('confirmArchivingDialog');
      this.saveProjectProperties({ projectId: this.selectedProject._id, newProjectProperties: { archived: true } });
      // this.updateProjectList();
    },
    saveProjectToDisk: function (project) {
      this.getProjectJson({ project }).then((projectData) => {
        var blob = new Blob([projectData], { type: 'text/json;charset=utf-8' });
        saveAs(blob, project.title);
      });
    },
    selectProjectFile: function () {
      var fileSelect = this.$refs.openFile;

      if (fileSelect) {
        fileSelect.click();
      }
    },
    loadProjectsFromDisk: function (e) {
      for (var file of e.target.files) {
        var reader = new FileReader();
        reader.onload = (x) => {
          var contents = x.target.result;
          var o = JSON.parse(contents);
          this.importProject({ projectData: o });
        };
        reader.readAsText(file);
      }
    },
    updateFilter: debounce(function (text) {
      this.filter = text;
    }, 400),
  },
  computed: {
    ...mapGetters(['hasRole', 'hasSimplePermissions', 'projectList', 'username']),

    projectLink: function () {
      return this.hasRole(Roles.SubContractor) || this.hasRole(Roles.Client) ? 'ProjectPlanEdit' : 'ProjectOverview';
    },

    filteredProjects() {
      if (!this.filter) {
        return this.projectList;
      }
      const r = new RegExp(getEscapedRegExpText(this.filter), 'i');
      return this.projectList.filter(({ projectNumber, title }) => {
        return r.test(`${projectNumber}${title}`);
      });
    },
  },
  components: {
    CommonFooter,
    CommonHeader,
    IsAdmin,
    ProjectSlide,
    FilterField,
  },
};
</script>

<style scoped>
h1 {
  font-size: 1.4em;
}

h2 {
  font-size: 1em;
}

h3 {
  font-size: 0.7em;
  font-weight: 400;
}

.project-list {
  width: 100%;
  display: flex;
  flex-flow: column;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  padding-top: 30px;
}

.project-box {
  color: white;
  padding: 10px;
  margin: 10px;
  text-align: center;
  position: relative;
  width: 100%;
  border: 2px solid #343a40;
  border-radius: 10px;
  transition: all 1s;
}

.project-action-icon {
  font-size: 2em;
}

@media (min-width: 768px) {
  main {
    text-align: center;
  }

  .project-list {
    flex-flow: row;
    flex-wrap: wrap;
    justify-content: center;
  }

  .project-box {
    width: 13em;
  }
}

.project-box-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.projectMenu {
  display: none;
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  background-color: rgba(0, 0, 0, 0.8);
  border-radius: 10px;
}

.projectMenuContents {
  top: 50%;
  position: relative;
  line-height: 17px;
  transform: translateY(-50%);
  padding: 15px;
}

.project-box:hover .projectMenu {
  display: block;
}

.project-icon {
  overflow: hidden;
  width: 160px;
  height: 160px;
  border: 1px solid black;
  display: inline-flex;
  border-radius: 80px;
  box-sizing: border-box;
}

.project-icon img {
  object-fit: cover;
  min-width: 160px;
  min-height: 160px;
  border-radius: 80px;
}

.projectIndicator {
  position: absolute;
}

.phone-project {
  display: none;
}

@media (max-width: 768px) {
  #new-project-box {
    display: none;
  }
  .project-list {
    justify-content: start;
  }

  .project-box {
    border: 0;
    /* background: linear-gradient(to right top,#171E26,#2d343f); */
    /* background: linear-gradient(to right top,#151e25,#2a2e35); */
    background: url(/static/item-back.jpg) no-repeat center;
    border-radius: 20px;
    width: 14em;
    min-height: 250px;
    background-size: 15em 260px;
  }
  .propjectBoxtitle {
    font-size: 130%;
    white-space: normal;
    overflow: auto;
    text-overflow: unset;
    margin-bottom: 5px;
    font-weight: normal;
  }
  .project-box h3 {
    color: #acaaae;
  }
}
@media (max-width: 767px) {
  main {
    background: url('/static/1.jpg') no-repeat center;
    height: 100vh;
  }
  .project-list {
    display: none !important;
  }
  .phone-project {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    padding-top: 80px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/projectlist/ProjectList.vue